import { create } from 'zustand';
import fetchWithTenant from '../utils/fetchWithTenant';
import useStore from './useStore';

const useAuthStore = create((set, get) => ({
  user: null,
  isAuthenticated: false,
    mustChangePassword: false,
  isLoading: true,

  // Multi-tenant state
  tenants: [],
  tenantVersion: 0,  // Increments on tenant switch to trigger re-renders           // All tenants user has access to
  currentTenant: null,   // Currently selected tenant
  tenantLoading: false,

  // Global config status
  globalConfig: {
    waveConfigured: false,
    feenicsConfigured: false,
    intercoms: []
  },

  // Load user's available tenants
  loadTenants: async () => {
        try {
      set({ tenantLoading: true });
      const response = await fetch('/api/user/tenants', { credentials: 'include' });
            if (response.ok) {
        const data = await response.json();
                const tenants = Array.isArray(data) ? data : (data.tenants || []);

        set({ tenants, tenantLoading: false });

        // Auto-select tenant if none selected
        if (!get().currentTenant && tenants.length > 0) {
          const savedTenantId = localStorage.getItem('currentTenantId');
          const savedTenant = savedTenantId && tenants.find(t => t.id === savedTenantId);

          if (savedTenant) {
            // Restore saved tenant
            await get().switchTenant(savedTenantId);
          } else {
            // Select first tenant by default
            await get().switchTenant(tenants[0].id);
          }
        }

        return tenants;
      }
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
    set({ tenantLoading: false });
    return [];
  },

  // Switch to a different tenant
  switchTenant: async (tenantId) => {
    try {
      set({ tenantLoading: true });
      const response = await fetch('/api/user/switch-tenant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tenantId })
      });

      if (response.ok) {
        const data = await response.json();
        const tenant = get().tenants.find(t => t.id === tenantId);

        // Update user.role to reflect the tenant-specific role
        const tenantRole = data.role || 'viewer';

        set(state => ({
          user: { ...state.user, role: tenantRole },
          currentTenant: {
            id: tenantId,
            name: tenant?.name || data.tenant?.name,
            organizationId: data.tenant?.organizationId,
            role: tenantRole,
            permissions: data.permissions || {},
            modules: data.modules || {},
            integrationTypes: data.integrationTypes || [],
            config: data.config
          },
          tenantLoading: false
        }));

        // Save to localStorage for persistence
        localStorage.setItem('currentTenantId', tenantId);

        // Clear old tenant data from useStore
        useStore.getState().clearTenantData();

        // Increment tenantVersion to signal all components to reset
        set({ tenantVersion: get().tenantVersion + 1 });
        console.log('[AUTH] Tenant switched, new version:', get().tenantVersion + 1);

        // Reset globalConfig while loading new tenant
        set({
          globalConfig: {
            waveConfigured: false,
            feenicsConfigured: false,
            intercoms: []
          }
        });

        // Reload global config for new tenant context
        await get().loadGlobalConfig();

        return { success: true };
      } else {
        const error = await response.json();
        set({ tenantLoading: false });
        return { success: false, error: error.error };
      }
    } catch (error) {
      set({ tenantLoading: false });
      return { success: false, error: error.message };
    }
  },

  // Load user settings from server
  loadUserSettings: async () => {
    try {
      const response = await fetch('/api/user/settings', { credentials: 'include' });
      if (response.ok) {
        const settings = await response.json();

        // Update the main store with user settings
        useStore.setState({
          gridConfig: settings.gridConfig || { cols: 2, rows: 2 },
          selectedLayout: settings.selectedLayout || 'all',
          sidebarCollapsed: settings.sidebarCollapsed || false,
          theme: settings.theme || 'dark',
          cameraOrder: settings.cameraOrder || [],
          settingsLoaded: true
        });
      }
    } catch (error) {
      console.error('Failed to load user settings:', error);
    }
  },

  // Load global config (Wave, Feenics status) - now tenant-aware
  loadGlobalConfig: async () => {
    try {
      // Capture current tenant at start of request
      const requestTenantId = localStorage.getItem('currentTenantId');

      // Use fetchWithTenant to automatically include tenant header
      const response = await fetchWithTenant('/api/global-config');

      // Check if tenant changed while request was in flight
      const currentTenantId = localStorage.getItem('currentTenantId');
      if (requestTenantId !== currentTenantId) {
        console.log('[AUTH] Tenant changed during request, ignoring stale response');
        return;
      }
      if (response.ok) {
        const config = await response.json();

        const isAdmin = get().isAdmin();
        console.log('[AUTH] isAdmin:', isAdmin, 'user role:', get().user?.role);

        if (isAdmin) {
          useStore.setState({
            waveConfig: config.waveConfig || {}
          });
          // Use waveConfigured from response (server already computed it)
          const isWaveConfigured = config.waveConfigured !== undefined
            ? config.waveConfigured
            : !!(config.waveConfig?.host);
          const isFeenicsConfigured = config.feenicsConfigured !== undefined
            ? config.feenicsConfigured
            : !!(config.feenicsConfig?.host && config.feenicsConfig?.instance);

          set({
            globalConfig: {
              waveConfigured: isWaveConfigured,
              feenicsConfigured: isFeenicsConfigured,
              intercoms: config.intercoms || [],
              waveConfig: config.waveConfig,
              feenicsConfig: config.feenicsConfig
            }
          });
        } else {
          // Set waveConfig in useStore so WebRTCPlayer works for non-admins
          if (config.waveConfig?.host) {
            useStore.setState({
              waveConfig: config.waveConfig
            });
          }
          set({
            globalConfig: {
              waveConfigured: config.waveConfigured,
              feenicsConfigured: config.feenicsConfigured,
              intercoms: config.intercoms || []
            }
          });
        }
      }
    } catch (error) {
      console.error('Failed to load global config:', error);
    }
  },

  // Check if user is logged in
  checkAuth: async () => {
    try {
      const response = await fetch('/api/auth/me', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();

        // Handle both old format (data.user) and new format (data directly)
        const user = data.user || data;

        set({
          user,
          isAuthenticated: true,
          isLoading: false
        });

        // Load tenants for all users (this will also load global config via switchTenant)
        await get().loadTenants();

        // Load user settings
        await get().loadUserSettings();

        // loadGlobalConfig is called by switchTenant, no need to call again

        return true;
      } else {
        set({ user: null, isAuthenticated: false, isLoading: false });
        return false;
      }
    } catch (error) {
      set({ user: null, isAuthenticated: false, isLoading: false });
      return false;
    }
  },

  // Login
  login: async (username, password, options = {}) => {
    try {
      const { extendSession = false, disclaimerAcceptedAt = null } = options;

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok && data.mfaRequired) {
        return { mfaRequired: true, pendingUserId: data.pendingUserId };
      }
      if (response.ok && data.success) {
        set({ user: data.user, isAuthenticated: true, mustChangePassword: data.mustChangePassword || false });

        // If extended session was requested, call the extend endpoint
        if (extendSession && disclaimerAcceptedAt) {
          try {
            await fetch('/api/sessions/extend', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ disclaimerAcceptedAt })
            });
            console.log('[AUTH] Session extended to 30 days');
          } catch (extendError) {
            console.error('Failed to extend session:', extendError);
            // Don't fail login if extend fails
          }
        }

        // Load tenants for all users
        await get().loadTenants();

        // Load user settings and global config
        await get().loadUserSettings();
        await get().loadGlobalConfig();

        return { success: true };
      } else {
        // Check for session-specific errors
        if (data.code === 'SESSION_REVOKED') {
          return { success: false, error: 'Your session has been terminated by an administrator.' };
        }
        if (data.code === 'SESSION_EXPIRED') {
          return { success: false, error: 'Your session has expired. Please log in again.' };
        }
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Logout
  logout: async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear tenant from localStorage
    localStorage.removeItem('currentTenantId');

    // Clear user settings from store
    useStore.setState({
      gridConfig: { cols: 2, rows: 2 },
      selectedLayout: 'all',
      sidebarCollapsed: false,
      theme: 'dark',
      cameraOrder: [],
      settingsLoaded: false,
      waveConfig: {}
    });

    set({
      user: null,
      isAuthenticated: false,
      tenants: [],
      currentTenant: null,
      globalConfig: {
        waveConfigured: false,
        feenicsConfigured: false,
        intercoms: []
      }
    });
  },

  // ── Role hierarchy helpers ──────────────────────────────────
  // Role precedence: admin (4) > manager (3) > operator (2) > viewer (1)
  // All helpers use currentTenant.role (set on switch) with user.role as fallback.

  hasRole: (requiredRole) => {
    const { user, currentTenant } = get();
    if (!user) return false;
    if (user.isPlatformAdmin) return true;

    const role = currentTenant?.role || user?.role;
    const roleHierarchy = { admin: 4, manager: 3, operator: 2, viewer: 1 };
    return (roleHierarchy[role] || 0) >= (roleHierarchy[requiredRole] || 0);
  },

  // Check if user is admin (includes platform admin)
  isAdmin: () => {
    const { user, currentTenant } = get();
    if (user?.isPlatformAdmin) return true;
    const role = currentTenant?.role || user?.role;
    return role === 'admin';
  },

  // Check if user is manager or higher (admin, manager)
  isManager: () => {
    const { user, currentTenant } = get();
    if (user?.isPlatformAdmin) return true;
    const role = currentTenant?.role || user?.role;
    return ['admin', 'manager'].includes(role);
  },

  // Check if user is platform admin
  isPlatformAdmin: () => get().user?.isPlatformAdmin === true,

  // Check if user is operator or higher
  isOperator: () => {
    const { user, currentTenant } = get();
    if (user?.isPlatformAdmin) return true;
    const role = currentTenant?.role || user?.role;
    return ['admin', 'manager', 'operator'].includes(role);
  },

  // Check tenant-level permission (from UserTenantAccess flags)
  hasTenantPermission: (permission) => {
    const { currentTenant, user } = get();

    // Platform admins have all permissions
    if (user?.isPlatformAdmin) return true;

    // Check tenant-specific permissions
    if (currentTenant?.permissions) {
      return currentTenant.permissions[permission] === true;
    }

    // Fallback to user-level permissions
    return user?.[permission] === true;
  },

  // Get current tenant ID for API calls
  getTenantHeader: () => {
    const { currentTenant } = get();
    return currentTenant ? { 'X-Tenant-ID': currentTenant.id } : {};
  }
}));

export default useAuthStore;
